import { Avatar, Box, InputAdornment, TextField, Typography } from '@material-ui/core'
import { AllSearchResult, BackendUser } from 'common/types'
import { useAnalytics } from 'hooks/use-analytics/useAnalytics'
import { useSearchBar } from 'hooks/use-search/useSearchBar'
import { Dispatch, FunctionComponent, SetStateAction, useLayoutEffect, useState } from 'react'
import styled from 'styled-components/macro'
//@ts-ignore
import { useTheme } from 'styled-components'
import { lighten } from 'polished'
import { useThemeSwitch } from 'hooks/theme'
import { Search } from '@material-ui/icons'
import { useHistory, useLocation } from 'react-router-dom'
import { useAuth } from 'pages/auth/hooks/useAuth'
import { truncateValues } from 'common/utils/truncateValues'
import { StyledEllipsisBox } from 'pages/content/components/posts/create-post/styled'

const StyledForm = styled(Box)`
  position: relative;
  margin: 0em 1.375em;
  width: auto;
  box-shadow: none;
  border: none;
  padding: 0;
`

const SearchInput = styled(TextField)<{ isDevice: boolean }>`
  & .MuiInput-input {
    background: no-repeat;
  }
  & .MuiInputBase-root {
    border-radius: 5.125em;
    padding: 1em 2.25em;
    height: 1.7em;
    width: ${props => (props.isDevice ? '100%' : '20.75em')};
    background-color: ${props => (props.isDevice ? props.theme.colors.supplementary : props.theme.colors.bgPrimary)};
  }
  && input {
    border: none;
    padding: 0;
  }
  &:focus {
    outline: none;
  }
`

const SearchResults = styled.ul<{ isDevice: boolean }>`
  position: ${props => (props.isDevice ? 'static' : 'absolute')};
  top: 80%;
  left: 100%;
  list-style: none;
  background: ${props => props.theme.colors.supplementary};
  color: ${props => props.theme.colors.accent1};
  padding: 2em 0 1.0625em 0;
  border-radius: 0.25em;
  margin: ${props => (props.isDevice ? '0.3em 0.8em 0 0.8em' : '1em 0 0 0')};
  width: ${props => (props.isDevice ? '25em' : '45.25em')};
  @media (min-width: ${props => props.theme.breakpoints.large}) {
    width: 45.25em;
  }
  z-index: 10000;
`
const SearchResultsItem = styled.li`
  display: flex;
  align-items: center;
  cursor: pointer;
  width: 100%;
  padding-left: 1.5625em;
  padding-top: 0.5625em;
  padding-bottom: 0.5625em;

  &.active {
    background: ${props => props.theme.colors.supplementary};
  }
`

const SearchResultsUsers = styled.div<{ isDevice?: boolean }>`
  display: flex;
  flex-direction: ${props => (props.isDevice ? 'column' : 'row')};
  margin-left: 1.5em;
  & > *:not(:last-child) {
    border-right: ${props => (props.isDevice ? 'none' : `1px solid ${props.theme.colors.accent}`)};
  }
  & > * {
    padding: ${props => (props.isDevice ? 'none' : '0 0.75em')};
  }
`
const UserInfoContainer = styled.div<{ isDevice?: boolean }>`
  max-width: ${props => (props.isDevice ? '8em' : '12.5em')};
  color: ${props => props.theme.colors.accent};
  @media (min-width: ${props => props.theme.breakpoints.large}) {
    max-width: '15.75em';
  }
`
const ResultsLink = styled(Box)`
  color: ${props => props.theme.colors.accent1};
  font-weight: bold;
  text-decoration: none;
  cursor: pointer;
`

const StyledHashtagResultWrapper = styled(Box)`
  && {
    margin: 0px 21px 17px 21px;
    cursor: pointer;
  }
`

const StyledScrollableContainer = styled(Box)`
  && {
    max-height: 25rem;
    overflow-y: scroll;
  }

  &::-webkit-scrollbar {
    display: none;
  }
`

const StyledSearchForContainer = styled(Box)`
  && {
    margin: 0px 21px;
    color: ${({ theme }) => theme.colors.supplementary1};
    display: flex;
    justify-content: flex-start;
    border-top: 2px solid ${({ theme }) => theme.colors.supplementary};
    padding-top: 1em;
  }
`
interface SearchBarProps {
  backendUser: BackendUser
  isDevice: boolean
  isTablet: boolean
  setSearchTab?: Dispatch<SetStateAction<number>>
  setPageNum?: Dispatch<SetStateAction<number>>
  setResults?: Dispatch<SetStateAction<AllSearchResult[]>>
}

export const SearchBar: FunctionComponent<SearchBarProps> = ({
  backendUser,
  isDevice,
  isTablet,
  setSearchTab,
  setPageNum,
  setResults
}) => {
  const {
    getListboxProps,
    getOptionProps,
    getRootProps,
    getInputProps,
    groupedOptions,
    setOpen,
    nameQuery,
    setNameQuery,
    results,
    hashtagResults,

    open,
    hashtagSearch,
    loading,
    getMoreHashtagResults,

    totalSuggestions,
    isValidSuggestion
  } = useSearchBar(backendUser)
  const history = useHistory()
  const { logEventAnalytics } = useAnalytics()
  const { backendPartner } = useAuth()
  const theme = useTheme()
  const { mode } = useThemeSwitch()
  const { pathname } = useLocation()
  const [searchResultIndex, setSearchResultsIndex] = useState(-1)

  const getThemeForInput = () => {
    return mode === 'light' ? lighten(0.9, theme.colors.accent1) : theme.colors.accent1
  }

  const handleSearchCategoryTracker = (eventName: string, Category: string) => {
    const logData = {
      'Partner ID': backendPartner?.id,
      'Company Name': backendPartner?.name,
      'Account Type': backendUser?.partnerId ? 'Partner' : backendUser?.role,
      Category
    }

    logEventAnalytics(
      backendUser?.partnerId ? 'Partner' : backendUser?.role,
      eventName,
      'mixpanel',
      eventName,
      {
        'Profile search query': nameQuery,
        'Profile search results': groupedOptions.length,
        'User ID': backendUser?.id,
        ...logData
      },
      backendUser?.id,
      {
        $name: `${backendUser?.firstName} ${backendUser?.lastName}`,
        $email: backendUser?.username
      }
    )
  }

  const checkUserIds = ({ loggedInUserId, id: resultsUserId, partnerName, ...rest }) => {
    const partnerLog = {}
    const userLog = {}
    if (backendUser?.partnerId) {
      partnerLog['Partner ID'] = backendPartner?.id
      partnerLog['Company Name'] = backendPartner?.name
      partnerLog['Account Type'] = 'Partner'
    } else {
      userLog['Account Type'] = backendUser?.role
    }

    if (loggedInUserId === resultsUserId) {
      history.push('/profile')
      setOpen(false)
    } else if (partnerName) {
      history.push(`/company/${resultsUserId}`)
      setOpen(false)
    } else {
      history.push(`/profile/${resultsUserId}`)
      logEventAnalytics(
        backendUser?.partnerId ? 'Partner' : backendUser?.role,
        `Profile viewed`,
        'mixpanel',
        'Profile view',
        {
          'Member profile viewed': resultsUserId,
          'ID of viewer': backendUser?.id,
          ...partnerLog,
          ...userLog
        },
        loggedInUserId,
        {
          $name: `${backendUser?.firstName} ${backendUser?.lastName}`,
          $email: backendUser?.username
        }
      )
      setOpen(false)
    }

    logEventAnalytics(
      backendUser?.partnerId ? 'Partner' : backendUser?.role,
      `Profile search made`,
      'mixpanel',
      'Profile search made',
      {
        'Profile query results': groupedOptions.length,
        'Profile query query': nameQuery,
        'User ID': backendUser?.id,
        ...partnerLog,
        ...userLog
      },
      loggedInUserId,
      {
        $name: `${backendUser?.firstName} ${backendUser?.lastName}`,
        $email: backendUser?.username
      }
    )
  }

  const getUserInfo = (user: any, question: string) => {
    if (user?.onboardingQuestions) {
      const answer = user?.onboardingQuestions?.find(user => user?.question === question)
      return answer?.answer
    } else if (user?.preferences) {
      const answer = user?.preferences?.find(user => user?.question === question)
      return answer?.answer
    }
    return ''
  }

  const renderName = result => {
    return result?.firstName ? `${result?.firstName} ${result?.lastName}` : result?.partnerName
  }

  const onKeyUp = (event: any) => {
    let listLength = 0

    if (groupedOptions?.length > 0) {
      listLength = groupedOptions?.length
    }

    if (searchResultIndex > listLength - 1) {
      setSearchResultsIndex(0)

      return
    }

    if (event.keyCode === 38) {
      if (searchResultIndex > -1) {
        setSearchResultsIndex(searchResultIndex - 1)
      }
    } else if (event.keyCode === 40) {
      if (searchResultIndex < listLength - 1) {
        setSearchResultsIndex(searchResultIndex + 1)
      }
    }
  }

  const renderSearchResults = () => {
    if (groupedOptions?.length > 0) {
      return (
        <SearchResults {...getListboxProps()} isDevice={isDevice} style={{ left: '10em' }}>
          {groupedOptions?.map((option, index) => {
            const isActive = searchResultIndex == index

            return (
              <>
                <SearchResultsItem
                  key={option?.id}
                  {...getOptionProps({ option, index })}
                  onClick={() => {
                    checkUserIds({ loggedInUserId: backendUser?.id, ...option })
                  }}
                  onMouseEnter={evt => {
                    setSearchResultsIndex(index)
                  }}
                  onMouseLeave={evt => {
                    setSearchResultsIndex(-1)
                  }}
                  id={`${option?.id}-search-result`}
                  className={isActive ? 'active' : ''}
                >
                  <Avatar
                    // @ts-ignore
                    src={option?.image || option?.logo}
                    alt="member with a profile"
                    style={{ width: '2.25em', height: '2.25em' }}
                  />
                  {!isDevice && option?.firstName && (
                    <SearchResultsUsers>
                      <Typography variant="body2">
                        <Box fontWeight="bold">{renderName(option)}</Box>
                      </Typography>
                      {option && getUserInfo(option, 'Headline') === '' ? null : (
                        <UserInfoContainer>
                          <Typography
                            variant="body2"
                            style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                          >
                            {getUserInfo(option, 'Headline')}
                          </Typography>
                        </UserInfoContainer>
                      )}
                      {option && getUserInfo(option, 'Company Name') === '' ? null : (
                        <UserInfoContainer>
                          <Typography
                            variant="body2"
                            style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                          >
                            {getUserInfo(option, 'Company Name')}
                          </Typography>
                        </UserInfoContainer>
                      )}
                    </SearchResultsUsers>
                  )}
                  {!isDevice && option?.partnerName && (
                    <SearchResultsUsers>
                      <Typography variant="body2">
                        <Box fontWeight="bold">{renderName(option)}</Box>
                      </Typography>
                      {option && getUserInfo(option, 'Industry') === '' ? null : (
                        <UserInfoContainer>
                          <Typography
                            variant="body2"
                            style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                          >
                            {getUserInfo(option, 'Industry')}
                          </Typography>
                        </UserInfoContainer>
                      )}
                    </SearchResultsUsers>
                  )}
                  {isDevice && option?.firstName && (
                    <SearchResultsUsers isDevice={isDevice}>
                      <Typography variant="body2">
                        <Box fontWeight="bold">{renderName(option)}</Box>
                      </Typography>
                      <Box display="flex">
                        {option && getUserInfo(option, 'Headline') === '' ? null : (
                          <UserInfoContainer isDevice={isDevice}>
                            <Typography
                              variant="body2"
                              style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                            >
                              {getUserInfo(option, 'Headline')}
                            </Typography>
                          </UserInfoContainer>
                        )}
                        {getUserInfo(option, 'Headline') !== '' && getUserInfo(option, 'Company Name') !== '' && (
                          <Box padding="0 0.5em">|</Box>
                        )}
                        {option && getUserInfo(option, 'Company Name') === '' ? null : (
                          <UserInfoContainer isDevice={isDevice}>
                            <Typography
                              variant="body2"
                              style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                            >
                              {getUserInfo(option, 'Company Name')}
                            </Typography>
                          </UserInfoContainer>
                        )}
                      </Box>
                    </SearchResultsUsers>
                  )}
                  {isDevice && option?.partnerName && (
                    <SearchResultsUsers isDevice={isDevice}>
                      <Typography variant="body2">
                        <Box fontWeight="bold">{renderName(option)}</Box>
                      </Typography>
                      {option && getUserInfo(option, 'Industry') === '' ? null : (
                        <UserInfoContainer>
                          <Typography
                            variant="body2"
                            style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                          >
                            {getUserInfo(option, 'Industry')}
                          </Typography>
                        </UserInfoContainer>
                      )}
                    </SearchResultsUsers>
                  )}
                </SearchResultsItem>
              </>
            )
          })}
          <Box
            color={theme.colors.supplementary1}
            display="flex"
            justifyContent={isDevice ? 'center' : 'space-around'}
            borderTop={`1px solid ${theme.colors.supplementary1}`}
            paddingTop="1em"
            flexDirection={isDevice ? 'column' : 'row'}
            alignItems={isDevice ? 'center' : 'unset'}
          >
            <Box display="flex" justifyContent="space-around" width={isDevice ? '100%' : '60%'}>
              <ResultsLink
                onClick={() => {
                  setOpen(false)
                  if (pathname === '/search') {
                    setSearchTab(0)
                    setPageNum(1)
                    setResults([])
                    history.push(`/search?name=${encodeURIComponent(nameQuery)}&city=&company=&country=`, {
                      tab: 0,
                      redirect: true
                    })
                  } else {
                    history.push(`/search?name=${encodeURIComponent(nameQuery)}&city=&company=&country=`, { tab: 0 })
                  }

                  handleSearchCategoryTracker('All results search made', 'All Results')
                }}
              >
                All Results
              </ResultsLink>
              <ResultsLink
                onClick={() => {
                  setOpen(false)
                  if (pathname === '/search') {
                    setSearchTab(1)
                    setPageNum(1)
                    setResults([])
                    history.push(`/search?name=${encodeURIComponent(nameQuery)}&city=&company=&country=`, {
                      tab: 1,
                      redirect: true
                    })
                  } else {
                    history.push(`/search?name=${encodeURIComponent(nameQuery)}&city=&company=&country=`, { tab: 1 })
                  }
                  handleSearchCategoryTracker('People search made', 'People')
                }}
              >
                People
              </ResultsLink>
              <ResultsLink
                onClick={() => {
                  setOpen(false)
                  if (pathname === '/search') {
                    setSearchTab(2)
                    setPageNum(1)
                    setResults([])
                    history.push(`/search?name=${encodeURIComponent(nameQuery)}&city=&company=&country=`, {
                      tab: 2,
                      redirect: true
                    })
                  } else {
                    history.push(`/search?name=${encodeURIComponent(nameQuery)}&city=&company=&country=`, { tab: 2 })
                  }
                  handleSearchCategoryTracker('Companies search made', 'Companies')
                }}
              >
                Companies
              </ResultsLink>
            </Box>
            <Box display="flex" justifyContent="space-around" width={isDevice ? '80%' : '40%'}>
              <ResultsLink
                onClick={() => {
                  setOpen(false)
                  if (pathname === '/search') {
                    setSearchTab(3)
                    setPageNum(1)
                    setResults([])
                    history.push(`/search?name=${encodeURIComponent(nameQuery)}&city=&company=&country=`, {
                      tab: 3,
                      redirect: true
                    })
                  } else {
                    history.push(`/search?name=${encodeURIComponent(nameQuery)}&city=&company=&country=`, { tab: 3 })
                  }
                  handleSearchCategoryTracker('Channels search made', 'Channels')
                }}
              >
                Channels
              </ResultsLink>
              <ResultsLink
                onClick={() => {
                  setOpen(false)
                  if (pathname === '/search') {
                    setSearchTab(4)
                    setPageNum(1)
                    setResults([])
                    history.push(`/search?name=${encodeURIComponent(nameQuery)}&city=&company=&country=`, {
                      tab: 4,
                      redirect: true
                    })
                  } else {
                    history.push(`/search?name=${encodeURIComponent(nameQuery)}&city=&company=&country=`, { tab: 4 })
                  }
                  handleSearchCategoryTracker('Communities search made', 'Communities')
                }}
              >
                Communities
              </ResultsLink>
            </Box>
          </Box>
        </SearchResults>
      )
    } else if (results?.length) {
      return (
        <>
          {open && hashtagSearch && isValidSuggestion && (
            <SearchResults {...getListboxProps()} isDevice={isDevice} style={{ left: '10em' }}>
              <StyledScrollableContainer>
                {hashtagResults?.map(tag => {
                  return (
                    <StyledHashtagResultWrapper
                      onClick={() => {
                        setNameQuery('')
                        setOpen(false)

                        window.open(`/feed/tag/${tag?.topic}`, '_self')
                      }}
                    >
                      <StyledEllipsisBox color={theme.colors.accent1}>
                        <Typography variant="body2">
                          <Box fontWeight="bold">#{tag?.topic}</Box>
                        </Typography>
                      </StyledEllipsisBox>
                      <StyledEllipsisBox fontSize={'0.75rem'} color={theme.colors.accent}>
                        {tag?.totalTopicCount && `${truncateValues(tag?.totalTopicCount)} Posts`}
                      </StyledEllipsisBox>
                    </StyledHashtagResultWrapper>
                  )
                })}

                <StyledSearchForContainer>
                  <ResultsLink
                    onClick={() => {
                      setOpen(false)
                    }}
                  >
                    {`Search for ${nameQuery}`}
                  </ResultsLink>
                </StyledSearchForContainer>
              </StyledScrollableContainer>
            </SearchResults>
          )}
        </>
      )
    } else {
      return null
    }
  }

  useLayoutEffect(() => {
    if (searchResultIndex > -1) {
      setSearchResultsIndex(-1)
    }
  }, [results])

  return (
    <>
      <StyledForm
        component="form"
        {...getRootProps()}
        style={{ display: 'block' }}
        onSubmit={e => {
          e.preventDefault()
          setOpen(false)
          history.push(`/search?name=${encodeURIComponent(nameQuery)}&city=&company=&country=`)
        }}
      >
        <SearchInput
          isDevice={isDevice}
          type="search"
          value={nameQuery || ''}
          onSelect={(e: any) => {
            setNameQuery(e?.target?.defaultValue)
            logEventAnalytics('Member', `Profile search made`, 'GA')
          }}
          {...getInputProps()}
          placeholder="Search..."
          InputProps={{
            disableUnderline: true,
            style: {
              border: 'none',
              color: getThemeForInput()
            },
            endAdornment: (
              <InputAdornment position="start">
                <Search
                  style={{ color: theme.colors.accent, cursor: 'pointer' }}
                  onClick={() => {
                    history.push(`/search?name=${encodeURIComponent(nameQuery)}&city=&company=&country=`)
                    setOpen(false)
                  }}
                />
              </InputAdornment>
            )
          }}
          onKeyDown={evt => {
            if (searchResultIndex > -1 && evt?.keyCode === 13) {
              const items = groupedOptions || []

              const selectedResultItem = items[searchResultIndex]

              if (selectedResultItem) {
                const targetEle = document.getElementById(`${selectedResultItem?.id}-search-result`)

                if (targetEle) {
                  targetEle?.click()
                }
              }
            }
          }}
          onKeyUp={evt => {
            onKeyUp(evt)
          }}
        />
      </StyledForm>
      {renderSearchResults()}
    </>
  )
}
